import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { ClickableNumber } from '@/components/ui/ClickableNumber';
import { HoverUnderlineSpan } from '@/components/ui/HoverUnderlineSpan';
import { usePermissions } from '@/hooks/usePermissions';
import { DashboardMetrics } from '@/services/metricsService';

interface OverdueInvoicesMiniCardProps {
  metrics: DashboardMetrics;
  onNavigate?: (page: string, data?: { columnFilters?: Record<string, string> }) => void;
}

export default function OverdueInvoicesMiniCard({ metrics, onNavigate }: OverdueInvoicesMiniCardProps) {
  const { hasPermission } = usePermissions();

  const overdueAmount = metrics.overdueAmount;
  const overdueCount = metrics.overdueInvoices;

  if (!hasPermission('view_overdue_invoices_mini_card')) {
    return null;
  }

  // Traffic light: based on number of overdue invoices
  // 🟢 Green = 0 overdue (all clear)
  // 🟡 Amber = 1–4 overdue (attention needed)
  // 🔴 Red   = 5+ overdue (action required)
  const badgeBg =
    overdueCount === 0
      ? 'var(--mc-success-bg)'
      : overdueCount <= 4
      ? 'var(--mc-warning-bg)'
      : 'var(--mc-error-bg)';
  const iconColor =
    overdueCount === 0
      ? 'var(--mc-success-icon)'
      : overdueCount <= 4
      ? 'var(--mc-warning-icon)'
      : 'var(--mc-error-icon)';

  const borderColor =
    overdueCount === 0
      ? 'var(--mc-success-border)'
      : overdueCount <= 4
      ? 'var(--mc-warning-border)'
      : 'var(--mc-error-border)';

  return (
    <Card className="h-full" style={{ borderLeft: `3px solid ${borderColor}` }}>
      <CardContent className="p-4 flex flex-col justify-between h-full">
        {/* Top row — label + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">Overdue Invoices</span>
            <InfoTooltip
              title="Overdue Invoices"
              content="Number of invoices past their due date. Click to see details and send reminders to customers."
            />
          </div>
          <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: badgeBg }}>
            <AlertCircle className="h-4 w-4" style={{ color: iconColor }} />
          </div>
        </div>

        {/* Value + sub-text */}
        <div>
          <div className="text-2xl font-bold text-neutral-900 tracking-tight">
            <HoverUnderlineSpan
              onClick={() => onNavigate?.('transactions', { columnFilters: { source: 'Sales Ledger', status: 'Overdue', transactionType: 'Invoice' } })}
              title="View overdue sales invoices"
            >
              {overdueCount}
            </HoverUnderlineSpan>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs font-semibold text-neutral-800">
              <ClickableNumber
                value={overdueAmount}
                onClick={() => onNavigate?.('transactions', { columnFilters: { source: 'Sales Ledger', status: 'Overdue', transactionType: 'Invoice' } })}
                showUnderline={true}
                className="text-xs font-semibold text-neutral-800"
              />
            </span>
            <span className="text-xs font-normal text-neutral-800">total overdue</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
